const TAB_ID_KEY = 'cwPhoneTabId';
const LOCK_PREFIX = 'cw-phone-tab:';

const hasLocks = typeof navigator !== 'undefined' && 'locks' in navigator;

const newId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getTabId = () => {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = newId();
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
};

// Try to acquire the lock for `id`. Resolves true when acquired (and then
// holds it for the whole lifetime of this tab — the browser releases it
// automatically when the document is destroyed). Resolves false when another
// live tab already holds it.
const tryHold = id =>
  new Promise(resolve => {
    navigator.locks.request(LOCK_PREFIX + id, { ifAvailable: true }, lock => {
      resolve(Boolean(lock));
      // A never-resolving promise keeps the lock until the tab is closed.
      return lock ? new Promise(() => {}) : undefined;
    });
  });

/**
 * Resolves true when this tab is a *duplicate* of an already-open tab.
 *
 * A duplicated tab inherits the source tab's sessionStorage (so the same tab
 * id) while the source is still alive holding that id's lock — acquisition
 * fails -> duplicate. A reloaded tab finds the lock free (its previous
 * instance is gone) -> not a duplicate. First load mints a fresh id -> the
 * lock is always free -> not a duplicate.
 */
export const isDuplicateTab = async () => {
  if (!hasLocks) return false; // can't detect -> don't block sign-in
  const id = getTabId();
  if (await tryHold(id)) return false; // free lock -> first load or reload

  // Lock busy -> a live tab owns this id -> we are a duplicate.
  // Adopt a fresh private id (and hold its lock) so that future reloads of
  // THIS tab are treated as normal same-tab reloads.
  const freshId = newId();
  sessionStorage.setItem(TAB_ID_KEY, freshId);
  await tryHold(freshId);
  return true;
};
